import Vue from 'vue'
import Router from 'vue-router'
import { wrapPage } from '../compat/nuxt-page'
import { getRuntime } from '../compat/runtime'

Vue.use(Router)

const originalPush = Router.prototype.push
const originalReplace = Router.prototype.replace

function isIgnorableNavigationError(error) {
  return error && ['NavigationDuplicated', 'NavigationAborted', 'NavigationCancelled'].includes(error.name)
}

Router.prototype.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject) {
    return originalPush.call(this, location, onResolve, onReject)
  }
  return originalPush.call(this, location).catch(error => {
    if (isIgnorableNavigationError(error)) {
      return this.currentRoute
    }
    return Promise.reject(error)
  })
}

Router.prototype.replace = function replace(location, onResolve, onReject) {
  if (onResolve || onReject) {
    return originalReplace.call(this, location, onResolve, onReject)
  }
  return originalReplace.call(this, location).catch(error => {
    if (isIgnorableNavigationError(error)) {
      return this.currentRoute
    }
    return Promise.reject(error)
  })
}

const pageModules = require.context('@/pages', true, /\.vue$/)

const load = file => wrapPage(pageModules(`./${file}`).default)

const routes = [
  {
    path: '/',
    component: load('index.vue'),
    alias: ['/index.html', '/pc', '/pc/', '/pc/index.html']
  },
  { path: '/coupon_center', component: load('coupon_center.vue') },
  { path: '/evaluation', component: load('evaluation.vue'), meta: { auth: 'guest' } },
  { path: '/goods_cate', component: load('goods_cate.vue') },
  { path: '/goods_coupon', component: load('goods_coupon.vue') },
  { path: '/goods_detail/:id', component: load('goods_detail/_id/index.vue') },
  { path: '/goods_list', component: load('goods_list.vue') },
  { path: '/goods_presell', component: load('goods_presell.vue') },
  { path: '/goods_presell_detail/:id', component: load('goods_presell_detail/_id/index.vue') },
  { path: '/goods_ranking/:cate_id', component: load('goods_ranking/_cate_id.vue') },
  { path: '/goods_search', component: load('goods_search.vue') },
  { path: '/goods_seckill', component: load('goods_seckill.vue') },
  { path: '/goods_seckill_detail/:id', component: load('goods_seckill_detail/_id/index.vue') },
  { path: '/login', component: load('login.vue') },
  { path: '/logistics', component: load('logistics.vue'), meta: { auth: 'guest' } },
  { path: '/logistics_delivery', component: load('logistics_delivery.vue'), meta: { auth: 'guest' } },
  { path: '/merchant_settled', component: load('merchant_settled.vue'), meta: { auth: 'guest' } },
  { path: '/news_detail/:id', component: load('news_detail/_id/index.vue') },
  { path: '/news_list', component: load('news_list.vue') },
  { path: '/order_confirm', component: load('order_confirm.vue'), meta: { auth: 'guest' } },
  { path: '/order_detail', component: load('order_detail.vue'), meta: { auth: 'guest' } },
  { path: '/order_stay_detail', component: load('order_stay_detail.vue'), meta: { auth: 'guest' } },
  { path: '/payment', component: load('payment.vue'), meta: { auth: 'guest' } },
  { path: '/privacy_agreement', component: load('privacy_agreement.vue'), meta: { auth: 'guest' } },
  { path: '/qualifications', component: load('qualifications.vue'), meta: { auth: 'guest' } },
  { path: '/refund', component: load('refund.vue'), meta: { auth: 'guest' } },
  { path: '/refund_confirm', component: load('refund_confirm.vue'), meta: { auth: 'guest' } },
  { path: '/refund_detail', component: load('refund_detail.vue'), meta: { auth: 'guest' } },
  { path: '/refund_goods', component: load('refund_goods.vue'), meta: { auth: 'guest' } },
  { path: '/refund_logistics', component: load('refund_logistics.vue'), meta: { auth: 'guest' } },
  { path: '/shop_more', component: load('shop_more.vue') },
  { path: '/shop_street', component: load('shop_street.vue') },
  { path: '/shopping_cart', component: load('shopping_cart.vue'), meta: { auth: 'guest' } },
  {
    path: '/store',
    component: load('store.vue'),
    children: [
      { path: '', component: load('store/index.vue') },
      { path: 'category', component: load('store/category.vue') },
      { path: 'storeCoupon', component: load('store/storeCoupon.vue') }
    ]
  },
  {
    path: '/user',
    component: load('user.vue'),
    meta: { auth: 'guest' },
    children: [
      { path: '', component: load('user/index.vue'), meta: { auth: 'guest' } },
      { path: 'address_list', component: load('user/address_list.vue'), meta: { auth: 'guest' } },
      { path: 'balance', component: load('user/balance.vue'), meta: { auth: 'guest' } },
      { path: 'collect', component: load('user/collect.vue'), meta: { auth: 'guest' } },
      { path: 'integral', component: load('user/integral.vue'), meta: { auth: 'guest' } },
      { path: 'invoice_list', component: load('user/invoice_list.vue'), meta: { auth: 'guest' } },
      { path: 'my_coupon', component: load('user/my_coupon.vue'), meta: { auth: 'guest' } },
      { path: 'order_list', component: load('user/order_list.vue'), meta: { auth: 'guest' } },
      { path: 'presell_order', component: load('user/presell_order.vue'), meta: { auth: 'guest' } },
      { path: 'refund_list', component: load('user/refund_list.vue'), meta: { auth: 'guest' } },
      { path: 'refund_select', component: load('user/refund_select.vue'), meta: { auth: 'guest' } },
      { path: 'settle_record', component: load('user/settle_record.vue'), meta: { auth: 'guest' } }
    ]
  }
]

const router = new Router({
  mode: 'history',
  routes,
  scrollBehavior() {
    return { x: 0, y: 0 }
  }
})

router.beforeEach(async (to, from, next) => {
  const runtime = getRuntime()
  const auth = runtime && runtime.auth
  const cookies = runtime && runtime.cookies
  const requiresAuth = to.matched.some(record => record.meta && record.meta.auth === 'guest')

  if (requiresAuth && auth && !auth.loggedIn) {
    cookies && cookies.set('fromPath', `${to.path}${to.fullPath.includes('?') ? to.fullPath.slice(to.path.length) : ''}`, { path: '/', maxAge: 60 * 60 * 24 })
    return next('/login')
  }

  if (to.path === '/login' && auth && auth.loggedIn) {
    return next('/')
  }

  next()
})

export default router
